/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect } from 'react';
import { FileInput } from 'flowbite-react';
import { BadgeInfo, CircleX, Star, X } from 'lucide-react';
import { UseFormRegister, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useUpFile from '../stores/useUpFile';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import Progress from './ui/progress';
import Input from './ui/input';
import useUpload from '../stores/useUpload';
import { FormField, FormItem, FormMessage } from './ui/form';

interface ImageUploadProps {
  setValue: UseFormSetValue<any>;
  register: UseFormRegister<any>;
  imageFieldName: string;
  required?: boolean;
  defaultImage?: string | null;
}

interface FileUploadSimpleProps {
  setValue: UseFormSetValue<any>;
  imageFieldName: string;
  required?: boolean;
  form: UseFormReturn<any>;
}

interface MultipleImageInputProps {
  onImagesChange: (data: { imageUrls: string[]; imageTitles: string[] }) => void;
  existingImages?: string[];
  existingTitles?: string[];
}

export default function MultipleImageInput({
  onImagesChange,
  existingImages = [],
  existingTitles = [],
}: MultipleImageInputProps) {
  const { loading, uploadFileMultiple } = useUpFile();
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageTitles, setImageTitles] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [showProgress, setShowProgress] = useState<boolean>(false);

  useEffect(() => {
    setShowProgress(true);
    if (!loading) {
      const timer = setTimeout(() => setShowProgress(false), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading]);

  useEffect(() => {
    const safeImages = Array.isArray(existingImages) ? existingImages : [];
    const safeTitles = Array.isArray(existingTitles) ? existingTitles : [];

    console.log('useEffect - safeImages:', safeImages);
    console.log('useEffect - safeTitles:', safeTitles);

    if (safeImages.length > 0) {
      setImageUrls(safeImages);
      setImageTitles(safeTitles.length > 0 ? safeTitles : Array(safeImages.length).fill(''));
      const initialProgress = safeImages.reduce((acc, url, index) => {
        acc[`existing-${index}`] = 100;
        return acc;
      }, {} as { [key: string]: number });
      setUploadProgress(initialProgress);
    }
  }, [existingImages, existingTitles]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (files) {
        const newImageUrls: string[] = [];
        const newImageTitles: string[] = [];

        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          const fileKey = `${file.name}-${i}`;

          setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));

          try {
            // eslint-disable-next-line no-await-in-loop
            const imageUrl = await uploadFileMultiple(file, (progress) => {
              setUploadProgress((prev) => ({ ...prev, [fileKey]: progress }));
            });

            if (imageUrl) {
              newImageUrls.push(imageUrl);
              newImageTitles.push('');
              setUploadProgress((prev) => ({ ...prev, [fileKey]: 100 }));
            } else {
              setUploadProgress((prev) => ({ ...prev, [fileKey]: -1 }));
            }
          } catch (error) {
            console.error('Error uploading file:', error);
            setUploadProgress((prev) => ({ ...prev, [fileKey]: -1 }));
          }
        }

        setImageUrls((prev) => [...prev, ...newImageUrls]);
        setImageTitles((prev) => [...prev, ...newImageTitles]);
        onImagesChange({
          imageUrls: [...imageUrls, ...newImageUrls],
          imageTitles: [...imageTitles, ...newImageTitles],
        });
      }
    },
    [uploadFileMultiple, onImagesChange, imageUrls, imageTitles],
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      const newImageUrls = [...imageUrls];
      const newImageTitles = [...imageTitles];
      newImageUrls.splice(index, 1);
      newImageTitles.splice(index, 1);

      const newProgress = { ...uploadProgress };
      const progressKeys = Object.keys(newProgress);
      delete newProgress[progressKeys[index]];

      setImageUrls(newImageUrls);
      setImageTitles(newImageTitles);
      setUploadProgress(newProgress);
      onImagesChange({ imageUrls: newImageUrls, imageTitles: newImageTitles });
    },
    [imageUrls, imageTitles, uploadProgress, onImagesChange],
  );

  const handleDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) return;

      const newImageUrls = [...imageUrls];
      const newImageTitles = [...imageTitles];
      const [reorderedUrl] = newImageUrls.splice(result.source.index, 1);
      const [reorderedTitle] = newImageTitles.splice(result.source.index, 1);
      newImageUrls.splice(result.destination.index, 0, reorderedUrl);
      newImageTitles.splice(result.destination.index, 0, reorderedTitle);

      setImageUrls(newImageUrls);
      setImageTitles(newImageTitles);
      onImagesChange({ imageUrls: newImageUrls, imageTitles: newImageTitles });
    },
    [imageUrls, imageTitles, onImagesChange],
  );

  const handleTitleChange = (index: number, value: string) => {
    const newImageTitles = [...imageTitles];
    newImageTitles[index] = value;
    setImageTitles(newImageTitles);
    onImagesChange({ imageUrls, imageTitles: newImageTitles });
  };

  const renderProgressText = (progress: number) => {
    if (progress < 100 && progress >= 0) return `Uploading ${progress}%`;
    if (progress === 100) return 'Done';
    if (progress === -1) return 'Upload Failed';
    return 'Error';
  };

  return (
    <div className="flex flex-col text-sm w-full mt-2 gap-2">
      <div className="flex items-center gap-1">
        <p>Images</p>
        <HoverCard>
          <HoverCardTrigger>
            <BadgeInfo className="w-4" />
          </HoverCardTrigger>
          <HoverCardContent>Kamu bisa mengunggah banyak gambar destinasi yang akan dikunjungi</HoverCardContent>
        </HoverCard>
      </div>
      <FileInput accept=".jpg, .jpeg, .png, .webp" multiple onChange={handleFileChange} />

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="images" direction="horizontal">
          {(providedDroppable) => (
            <div
              ref={providedDroppable.innerRef}
              {...providedDroppable.droppableProps}
              className="grid grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 mt-2"
            >
              {Object.keys(uploadProgress).length === 0 && imageUrls.length === 0 ? (
                <img
                  src="/assets/no-data-image.svg"
                  className="w-28 h-32 object-cover border border-border rounded-lg"
                  alt="No data"
                />
              ) : (
                Object.keys(uploadProgress).map((fileKey, index) => (
                  <Draggable key={fileKey} draggableId={fileKey} index={index}>
                    {(providedDraggable) => (
                      <div
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        {...providedDraggable.dragHandleProps}
                        className="relative min-h-28 bg-white border overflow-hidden border-border rounded-lg flex flex-col justify-between"
                      >
                        {uploadProgress[fileKey] === 100 && imageUrls[index] ? (
                          <img
                            src={imageUrls[index]}
                            className="w-full h-28 object-cover rounded-t-lg"
                            alt={`uploaded-${index}`}
                          />
                        ) : (
                          <div className="w-full h-28 bg-gray-200 flex items-center justify-center rounded-t-lg">
                            <p className="text-gray-500">Uploading...</p>
                          </div>
                        )}

                        {/* Progress Bar */}
                        {showProgress && (
                          <div className="px-2 py-1">
                            {uploadProgress[fileKey] === -1 ? (
                              <p className="text-xs text-center mt-1 text-red-600">Upload Failed</p>
                            ) : (
                              <>
                                <Progress value={uploadProgress[fileKey]} className="w-full h-1" />
                                <p className="text-xs text-center mt-1">
                                  {renderProgressText(uploadProgress[fileKey])}
                                </p>
                              </>
                            )}
                          </div>
                        )}

                        {/* Input Judul */}
                        {uploadProgress[fileKey] === 100 && (
                          <div className="p-2">
                            <Input
                              value={imageTitles[index] || ''}
                              onChange={(e) => handleTitleChange(index, e.target.value)}
                              placeholder="Judul"
                              className="text-xs"
                            />
                          </div>
                        )}

                        {/* Tombol Hapus */}
                        {uploadProgress[fileKey] === 100 && (
                          <button
                            type="button"
                            className="absolute top-1 right-1 bg-secondary/50 hover:bg-secondary text-white px-2 py-1 rounded-full text-xs"
                            onClick={() => handleRemoveImage(index)}
                          >
                            <X className="w-4" />
                          </button>
                        )}

                        {/* Label Gambar Utama */}
                        {index === 0 && uploadProgress[fileKey] === 100 && (
                          <span className="absolute top-2 left-1 bg-secondary text-white text-xs px-2 py-1 rounded">
                            <Star />
                          </span>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {providedDroppable.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

export function FileUpload({ setValue, register, imageFieldName, required, defaultImage }: ImageUploadProps) {
  const { uploadFile, loading, onProgress } = useUpFile();
  const [showUrl, setUrl] = useState<string | null>();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showProgress, setShowProgress] = useState<boolean>(false);

  useEffect(() => {
    setShowProgress(true);
    if (onProgress === 100) {
      const timer = setTimeout(() => {
        setShowProgress(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [onProgress]);

  useEffect(() => {
    setUrl(defaultImage);
  }, [defaultImage]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setUploadProgress(0);
        let progress = 0;

        const interval = setInterval(() => {
          progress += 10;
          setUploadProgress(progress);
          if (progress >= 100) clearInterval(interval);
        }, 300);

        const imageUrl = await uploadFile(file);

        if (imageUrl) {
          setUrl(imageUrl);
          setValue(imageFieldName, imageUrl);
          setUploadProgress(100);
          clearInterval(interval);
        } else {
          clearInterval(interval);
          setUploadProgress(-1);
        }
      } catch (error) {
        console.error('Error handling file change:', error);
        setUploadProgress(-1);
      }
    }
  };
  const renderProgressText = (progress: number) => {
    if (progress < 100 && progress >= 0) return `Uploading ${progress}%`;
    if (progress === 100) return 'Done';
    if (progress === -1) return 'Upload Failed';
    return 'Error';
  };

  return (
    <div className="flex flex-col text-sm w-full gap-2">
      <FileInput title="Masukkan logo asuransi..." accept=".jpg, .jpeg, .png, .webp" onChange={handleFileChange} />

      {uploadProgress === 0 ? (
        <img
          src={showUrl || '/assets/no-data-image.svg'}
          className="w-28 h-32 object-cover border border-border rounded-lg"
          alt="No data"
        />
      ) : (
        <div className="relative w-fit h-fit bg-white border overflow-hidden border-border rounded-lg flex flex-col justify-between">
          {uploadProgress === 100 && !loading ? (
            <img
              src={showUrl || '/assets/no-data-image.svg'}
              alt="uploaded result"
              className="min-w-28 h-32 object-cover rounded-t-lg"
            />
          ) : (
            <div className="w-28 h-32 bg-gray-200 flex items-center justify-center rounded-t-lg">
              <p className="text-gray-500">Uploading...</p>
            </div>
          )}

          {showProgress && (
            <div className="px-2 py-1">
              {uploadProgress === -1 ? (
                <p className="text-xs text-center mt-1 text-red-600">Upload Failed</p>
              ) : (
                <>
                  <Progress value={onProgress} className="w-full h-1" />
                  <p className="text-xs text-center mt-1">{renderProgressText(onProgress)}</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
      <Input
        className="hidden"
        {...register(imageFieldName)}
        type="text"
        name={imageFieldName}
        id={imageFieldName}
        required={required || true}
      />
    </div>
  );
}

export function FileUploadSimple({ setValue, imageFieldName, form }: FileUploadSimpleProps) {
  const { uploadFile } = useUpload();
  const [showUrl, setUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const handleRemoveFile = () => {
    setUrl(null);
    setUploadProgress(0);
    setValue(imageFieldName, '');
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setUploadProgress(0);

        const imageUrl = await uploadFile(file, (progress) => {
          setUploadProgress(progress);
        });

        setUploadProgress(100);

        if (imageUrl) {
          setValue(imageFieldName, imageUrl, { shouldValidate: true });
          setUrl(imageUrl);
        }
      } catch (error) {
        console.error('Error handling file change:', error);
        setUploadProgress(-1);
      }
    }
  };

  const renderProgressText = (progress: number) => {
    if (progress < 100 && progress >= 0) return `Uploading ${progress}%`;
    if (progress === 100) return 'Done';
    return 'Error';
  };

  return (
    <FormField
      name={imageFieldName}
      control={form?.control}
      render={({ field }) => (
        <div className="flex relative items-center justify-center w-36 h-36 md:w-60 md:h-60">
          <FormItem>
            {uploadProgress === 0 ? (
              <label
                htmlFor={`file-${imageFieldName}`} // Menggunakan imageFieldName untuk membuat ID unik
                className="flex flex-col items-center justify-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 w-36 h-36 md:w-60 md:h-60"
              >
                <div className="flex flex-col items-center justify-center">
                  <svg
                    className="w-8 h-8 md:mb-4 mb-1 text-gray-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="mb-2 text-xs md:text-sm text-gray-500 flex flex-col gap-1 text-center">
                    <span className="font-semibold">Click to upload {imageFieldName}</span> or drag and drop
                  </p>
                </div>
                <input
                  id={`file-${imageFieldName}`}
                  type="file"
                  className="hidden"
                  accept=".jpg, .jpeg, .png, .webp"
                  onChange={handleFileChange}
                />
              </label>
            ) : (
              <div className="relative w-36 h-36 md:w-60 md:h-60 bg-white border border-border rounded-lg flex flex-col justify-between">
                {uploadProgress === 100 && showUrl ? (
                  <>
                    <img
                      src={showUrl || '/assets/no-data-image.svg'}
                      alt={`uploaded ${imageFieldName}`}
                      className="w-36 h-28 md:w-60 md:h-60 object-cover rounded-t-lg"
                    />
                    <button
                      className="absolute top-1 right-1 text-white/35 hover:text-red-600/70 bg-primary/70 transition-all p-1 rounded-full"
                      onClick={handleRemoveFile}
                      type="button"
                    >
                      <CircleX />
                    </button>
                  </>
                ) : (
                  <div className="w-36 h-36 md:w-60 md:h-60 bg-gray-200 flex items-center justify-center rounded-t-lg">
                    <p className="text-gray-500">Uploading {imageFieldName}...</p>
                  </div>
                )}

                <div className="px-2 py-1">
                  <Progress value={uploadProgress} className="w-full h-1" />
                  <p className="text-xs text-center mt-1">{renderProgressText(uploadProgress)}</p>
                </div>
              </div>
            )}

            <Input className="hidden" type="text" {...field} />
            <FormMessage />
          </FormItem>
        </div>
      )}
    />
  );
}
